import React, { useState, useEffect } from 'react';

// Emotion Circle Component
const EmotionCircle = ({ percentage, color, label, count }) => {
  const circleSize = 90;
  const strokeWidth = 8;
  const radius = (circleSize - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: circleSize, height: circleSize }}>
        <svg width={circleSize} height={circleSize} className="-rotate-90 transform">
          <circle cx={circleSize / 2} cy={circleSize / 2} r={radius} fill="#E6EDF7" stroke="none" />
          <circle cx={circleSize / 2} cy={circleSize / 2} r={radius - 5} fill="white" stroke="none" />
          {percentage > 0 && (
            <circle
              cx={circleSize / 2}
              cy={circleSize / 2}
              r={radius}
              fill="transparent"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={strokeDashoffset}
              strokeLinecap="round"
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center text-sm font-bold">{percentage}%</div>
      </div>
      <div className={`mt-1 text-center text-xs px-6 py-1 text-white rounded-lg `} style={{backgroundColor:color}}>{label}</div>
      <div className="text-sm font-semibold">{count}</div>
    </div>
  );
};

// Progress Bar Component
const ProgressBar = ({ settledPercentage, unsettledPercentage }) => (
  <div>
    <div className="mb-1 flex justify-between text-xs">
      <div>{settledPercentage}% Settled</div>
      <div>{unsettledPercentage}% Unsettled</div>
    </div>
    <div className="flex h-4 w-full overflow-hidden rounded-lg">
      <div className="bg-teal-200" style={{ width: `${settledPercentage}%` }} />
      <div className="bg-red-200" style={{ width: `${unsettledPercentage}%` }} />
    </div>
  </div>
);

// Pie Chart Component - Update size from 180 to 220
const PieChart = ({ segments }) => {
  const pieSize = 220; // Increased from 180 to 220
  const centerX = pieSize / 2;
  const centerY = pieSize / 2;
  const radius = pieSize * 0.45; // Adjusted for the larger size

  // Filter out segments with 0 percentage before calculating
  const filteredSegments = segments.filter(segment => segment.percentage > 0);
  
  // Calculate total percentage of all segments
  const totalPercentage = filteredSegments.reduce((sum, segment) => sum + segment.percentage, 0);
  
  // If there are no valid segments, show an empty circle
  if (filteredSegments.length === 0) {
    return (
      <svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`}>
        <circle cx={centerX} cy={centerY} r={radius} fill="#E6EDF7" stroke="none" />
        <text x={centerX} y={centerY} textAnchor="middle" dominantBaseline="middle" className="text-sm">
          No data
        </text>
      </svg>
    );
  }
  
  // If there's only one segment with 100%, draw a full circle
  if (filteredSegments.length === 1 && filteredSegments[0].percentage === 100) {
    return (
      <svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`}>
        <circle cx={centerX} cy={centerY} r={radius} fill={filteredSegments[0].color} stroke="none" />
        <text 
          x={centerX} 
          y={centerY} 
          textAnchor="middle" 
          dominantBaseline="middle" 
          fill="white" 
          className="text-base font-bold" // Increased font size for better readability
        >
          100%
        </text>
      </svg>
    );
  }

  // Start with an array of paths
  const paths = [];
  
  // Add gray background circle if total percentage is less than 100%
  if (totalPercentage < 100) {
    paths.push(
      <circle 
        key="background" 
        cx={centerX} 
        cy={centerY} 
        r={radius} 
        fill="#E6EDF7" 
        stroke="none" 
      />
    );
  }
  
  // For multiple segments, draw the pie chart
  let cumulativeAngle = 0;
  
  filteredSegments.forEach((segment, index) => {
    const startAngle = cumulativeAngle;
    const angleRadians = (segment.percentage / 100) * 2 * Math.PI;
    cumulativeAngle += angleRadians;

    const x1 = centerX + radius * Math.cos(startAngle);
    const y1 = centerY + radius * Math.sin(startAngle);
    const x2 = centerX + radius * Math.cos(startAngle + angleRadians);
    const y2 = centerY + radius * Math.sin(startAngle + angleRadians);

    const largeArcFlag = segment.percentage > 50 ? 1 : 0;

    const pathData = `M ${centerX} ${centerY} L ${x1} ${y1} A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2} Z`;

    paths.push(
      <g key={index}>
        <path d={pathData} fill={segment.color} />
        {segment.percentage >= 5 && (
          <text 
            x={centerX + radius * 0.6 * Math.cos(startAngle + angleRadians / 2)} 
            y={centerY + radius * 0.6 * Math.sin(startAngle + angleRadians / 2)} 
            textAnchor="middle" 
            dominantBaseline="middle" 
            fill="white" 
            className="text-sm font-bold" // Increased font size
          >
            {segment.percentage}%
          </text>
        )}
      </g>
    );
  });

  return (
    <svg width={pieSize} height={pieSize} viewBox={`0 0 ${pieSize} ${pieSize}`}>
      {paths}
    </svg>
  );
};

const InfoGraphicReportPreview = ({ data }) => {
  const [reportData, setReportData] = useState(null);

  useEffect(() => {
    if (!data) {
      return;
    }

    try {
      const jsonData = data;
      const FALLBACK_COLOR = "#c9d6e3";

      // Prefer the dynamic block (id/code-based, includes the new feelings and
      // positive reasons). Fall back to legacy keys if it isn't present.
      const summary = jsonData?.dynamic?.summary;

      let emotionsList = [];
      let highestEmotionName = "N/A";
      let highestEmotionPercentage = 0;
      let settled = 0;
      let unsettled = 0;
      let overall = 0;
      let mainFactor = "None";
      let mainFactorPercentage = 0;
      let availableReasons = [];

      if (summary) {
        // ---- Dynamic path ----
        const feelings = summary.feelings || [];

        emotionsList = feelings.map((f) => ({
          name: f.name,
          color: f.color || FALLBACK_COLOR,
          percentage: Math.round(Number(f.percentage) || 0),
          count: f.count || 0,
        }));

        const topFeeling = feelings.reduce((top, f) => (f.count > (top?.count || 0) ? f : top), null);
        if (topFeeling) {
          highestEmotionName = topFeeling.name;
          highestEmotionPercentage = Math.round(Number(topFeeling.percentage) || 0);
        }

        const polaritySplit = summary.polarity_split || {};
        settled = Math.round(Number(polaritySplit.healthy_percentage) || 0);
        unsettled = Math.round(Number(polaritySplit.unhealthy_percentage) || 0);
        overall = settled;

        availableReasons = (summary.negative_reasons || [])
          .filter((r) => (Number(r.percentage) || 0) > 0)
          .map((r) => ({ name: r.name, percentage: Number(r.percentage) || 0, color: r.color || FALLBACK_COLOR }));

        const topReason = availableReasons.reduce((top, r) => (r.percentage > (top?.percentage || 0) ? r : top), null);
        if (topReason) {
          mainFactor = topReason.name;
          mainFactorPercentage = topReason.percentage;
        }
      } else {
        // ---- Legacy fallback path ----
        const rp = jsonData.ResponsePercentage || {};
        const totalResponses =
          (rp.greatTotal || 0) + (rp.okTotal || 0) + (rp.mehTotal || 0) + (rp.lowTotal || 0) + (rp.strugglingTotal || 0);

        const legacyFeelings = [
          { name: "Great", color: "#FF9E9E", count: rp.greatTotal || 0 },
          { name: "Okay", color: "#FFBE9D", count: rp.okTotal || 0 },
          { name: "Meh", color: "#FFE6BC", count: rp.mehTotal || 0 },
          { name: "Low", color: "#80DBC8", count: rp.lowTotal || 0 },
          { name: "Struggling", color: "#1D3557", count: rp.strugglingTotal || 0 },
        ];

        emotionsList = legacyFeelings.map((f) => ({
          ...f,
          percentage: totalResponses > 0 ? Math.round((f.count / totalResponses) * 100) : 0,
        }));

        const topFeeling = legacyFeelings.reduce((top, f) => (f.count > (top?.count || 0) ? f : top), null);
        if (topFeeling) {
          highestEmotionName = topFeeling.name;
          highestEmotionPercentage = totalResponses > 0 ? Math.round((topFeeling.count / totalResponses) * 100) : 0;
        }

        settled = rp.greate_ok_percentage || 0;
        unsettled = rp.meh_low_struggling_percentage || 0;
        overall = settled;

        const reasonTypes = [
          { key: "poorSleep_percentage", name: "Poor Sleep", color: "#264254" },
          { key: "unwell_percentage", name: "Unwell", color: "#FEDEBC" },
          { key: "relationship_problems_percentage", name: "Relationship Problems", color: "#FCEBB0" },
          { key: "workStress_percentage", name: "Work Related Stress", color: "#F0D2C9" },
          { key: "homeLifeDifficulty_percentage", name: "Home Life Difficulties", color: "#FFBF9D" },
          { key: "bereavement_percentage", name: "Bereavement", color: "#B6E7CF" },
          { key: "anxiety_percentage", name: "Anxiety", color: "#B2C7D9" },
          { key: "cost_of_living_percentage", name: "Cost Of Living", color: "#F7E6AD" },
          { key: "depression_percentage", name: "Depression", color: "#F6B6B6" },
          { key: "micro_stress_percentage", name: "Micro Stress", color: "#D6E8DB" },
          { key: "ongoing_health_condition_percentage", name: "Ongoing Health Condition", color: "#FFE0C7" },
          { key: "menstration_percentage", name: "Menstration", color: "#F5CEDC" },
          { key: "parental_percentage", name: "Parental", color: "#C3DDC3" },
          { key: "pregnancy_percentage", name: "Pregnancy Related", color: "#E7D1F4" },
          { key: "mental_health_percentage", name: "Mental Health Rest Day", color: "#e8c7fd" },
          { key: "unknown_percentage", name: "Unknown", color: "#c9d6e3" },
        ];

        const rsp = jsonData.ReasonPercentage || {};
        reasonTypes.forEach((reason) => {
          if (rsp[reason.key] !== undefined && rsp[reason.key] > 0) {
            availableReasons.push({ name: reason.name, percentage: rsp[reason.key], color: reason.color });
            if (rsp[reason.key] > mainFactorPercentage) {
              mainFactor = reason.name;
              mainFactorPercentage = rsp[reason.key];
            }
          }
        });
      }

      // Format the dates
      const startDate = new Date(parseInt(jsonData.start_date) * 1000);
      const endDate = new Date(parseInt(jsonData.end_date) * 1000);
      const period = `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;

      // Extract the most recent evaluation
      const latestEvaluation = jsonData.Evaluation && jsonData.Evaluation.length > 0 ? jsonData.Evaluation[0] : null;

      // Transform data
      const transformedData = {
        period,
        organizationName: jsonData.organization_name || "ORGANIZATION",
        introText: "Annual wellbeing report based on collected staff data",
        workplaceHealth: {
          overall,
          settled,
          unsettled,
        },
        highestEmotion: {
          name: highestEmotionName,
          percentage: highestEmotionPercentage,
        },
        emotionsList,
        lowMoodFactors: {
          mainFactor,
          mainFactorPercentage,
          availableReasons,
        },
        evaluation: latestEvaluation ? latestEvaluation.evaluation : "",
        action: latestEvaluation ? latestEvaluation.action : "",
      };

      setReportData(transformedData);
    } catch (error) {
      console.error("Error processing data:", error);
    }
  }, [data]);

  if (!data) {
    return <div className="p-6 text-center">No data provided</div>;
  }

  if (!reportData) {
    return <div className="p-6 text-center">Processing data...</div>;
  }

  console.log("Rendering with report data:", reportData);

  return (
    <div className="mx-auto max-w-3xl overflow-hidden rounded-lg bg-gray-50 shadow-lg">
      {/* Report Header */}
      <div className="p-6">
        <div className="mb-4 flex items-center justify-between">
          <div className="rounded-full border border-gray-700 px-4 py-1">
            <span className="text-xs text-gray-700">STATISTICAL INFOGRAPHIC</span>
          </div>
          <div className="text-xs text-gray-700">{reportData.period}</div>
        </div>

        {/* Title */}
        <h1 className="mb-2 text-3xl font-bold text-gray-800">OVERVIEW FOR {reportData.organizationName}</h1>
        <p className="mb-6 text-sm text-gray-600">{reportData.introText}</p>

        {/* Workplace Health */}
        <div className="mb-6">
          <h2 className="mb-2 text-lg font-semibold text-gray-800">Overall workplace health: {reportData.workplaceHealth.overall}%</h2>
          <ProgressBar 
            settledPercentage={reportData.workplaceHealth.settled || 0} 
            unsettledPercentage={reportData.workplaceHealth.unsettled || 0} 
          />
        </div>

        {/* Emotions Card */}
        <div className="mb-6 rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
          <h2 className="mb-2 text-lg font-semibold">
            Highest ranking emotion: <span>{reportData.highestEmotion.name}</span>
          </h2>
          <p className="mb-6 text-sm">
            <span className="font-bold">{reportData.highestEmotion.percentage}%</span> of the team reported feeling{" "}
            {reportData.highestEmotion.name.toLowerCase()}, indicating overall positive workplace culture!
          </p>

          <div className="mb-4 flex flex-wrap justify-around gap-y-4">
            {reportData.emotionsList.map((emotion, index) => (
              <EmotionCircle
                key={index}
                percentage={emotion.percentage || 0}
                color={emotion.color}
                label={emotion.name}
                count={emotion.count || 0}
              />
            ))}
          </div>
        </div>

        <div className="my-6 border-b border-gray-200"></div>

        {/* Low Mood Contributors */}
        <div className="mb-6 rounded-lg border border-gray-100 bg-white p-6 shadow-sm">
          <h2 className="mb-2 text-lg">Biggest contributor to low mood: <span className="mb-2 text-lg font-semibold">{reportData.lowMoodFactors.mainFactor}</span></h2>
          <p className="mb-6 text-sm">
            <span className="font-bold">{reportData.lowMoodFactors.mainFactorPercentage}%</span> of reported low mood issues 
            were related to {reportData.lowMoodFactors.mainFactor.toLowerCase()}.
          </p>

          <div className="flex flex-wrap">
            <div className="flex w-full justify-center pb-4 md:w-2/3 md:pb-0"> {/* Changed from 3/5 to 2/3 */}
              <PieChart
                segments={reportData.lowMoodFactors.availableReasons.map(reason => ({
                  percentage: reason.percentage,
                  color: reason.color
                }))}
              />
            </div>
            <div className="flex w-full flex-col justify-center md:w-1/3"> {/* Changed from 2/5 to 1/3 */}
              {reportData.lowMoodFactors.availableReasons.map((reason, index) => (
                <div key={index} className="mb-2 flex items-center">
                  <div className="mr-2 h-3 w-3" style={{ backgroundColor: reason.color }}></div>
                  <span className="text-xs">{reason.name.toUpperCase()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InfoGraphicReportPreview;